import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../config';

const WhatsAppMessages = () => {
    const [messages, setMessages] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        delivered: 0,
        read: 0,
        responded: 0,
        failed: 0,
        undelivered: 0,
        sent: 0,
        pending: 0
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalMessages: 0,
        messagesPerPage: 50
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    useEffect(() => {
        fetchMessages();
    }, [pagination.currentPage, filterStatus]);

    const fetchMessages = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('userToken');
            
            const params = {
                page: pagination.currentPage,
                limit: 50
            };
            
            if (filterStatus) {
                params.status = filterStatus;
            }
            
            const response = await axios.get(`${apiBaseUrl}/api/admin/whatsapp-status`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                params
            });

            if (response.data.success) {
                setMessages(response.data.messages);
                setStats(response.data.stats);
                setPagination(response.data.pagination);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching WhatsApp messages:', err);
            setError('Error al cargar mensajes de WhatsApp');
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            delivered: { color: '#27ae60', icon: '✅', label: 'Entregado' },
            sent: { color: '#3498db', icon: '📤', label: 'Enviado' },
            failed: { color: '#e74c3c', icon: '❌', label: 'Falló' },
            undelivered: { color: '#e67e22', icon: '⚠️', label: 'No entregado' },
            read: { color: '#9b59b6', icon: '👁️', label: 'Leído' },
            queued: { color: '#95a5a6', icon: '⏳', label: 'En cola' },
            sending: { color: '#f39c12', icon: '📡', label: 'Enviando' }
        };

        const config = statusConfig[status] || { color: '#95a5a6', icon: '❓', label: status || 'Desconocido' };

        return (
            <span style={{
                padding: '6px 12px',
                borderRadius: '15px',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: 'white',
                background: config.color,
                display: 'inline-block',
                whiteSpace: 'nowrap'
            }}>
                {config.icon} {config.label}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('es-AR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleFilterChange = (status) => {
        setFilterStatus(status);
        setPagination({ ...pagination, currentPage: 1 });
    };

    const handlePageChange = (newPage) => {
        setPagination({ ...pagination, currentPage: newPage });
    };

    if (loading && messages.length === 0) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', color: '#7f8c8d' }}>Cargando mensajes...</div>
            </div>
        );
    }

    return (
        <div style={{ padding: '30px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '30px' }}>
                <h1 style={{ 
                    fontSize: '2rem', 
                    fontWeight: '700', 
                    color: '#2c3e50',
                    marginBottom: '10px'
                }}>
                    📱 Mensajes de WhatsApp
                </h1>
                <p style={{ color: '#7f8c8d', fontSize: '1rem' }}>
                    Seguimiento de mensajes enviados y estado de entrega
                </p>
            </div>

            {/* Stats Summary */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                }}>
                    <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '5px' }}>Total Mensajes</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700' }}>{stats.total}</div>
                </div>

                <div style={{
                    background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(39, 174, 96, 0.3)'
                }}>
                    <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '5px' }}>✅ Entregados</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700' }}>{stats.delivered}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                        {stats.total > 0 ? `${((stats.delivered / stats.total) * 100).toFixed(1)}%` : '0%'}
                    </div>
                </div>

                <div style={{
                    background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(52, 152, 219, 0.3)'
                }}>
                    <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '5px' }}>👁️ Leídos</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700' }}>{stats.read}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                        {stats.total > 0 ? `${((stats.read / stats.total) * 100).toFixed(1)}%` : '0%'}
                    </div>
                </div>

                <div style={{
                    background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(155, 89, 182, 0.3)'
                }}>
                    <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '5px' }}>💬 Respondieron</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700' }}>{stats.responded}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                        {stats.total > 0 ? `${((stats.responded / stats.total) * 100).toFixed(1)}%` : '0%'}
                    </div>
                </div>

                <div style={{
                    background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(231, 76, 60, 0.3)'
                }}>
                    <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '5px' }}>❌ Fallidos</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700' }}>{stats.failed}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                        {stats.total > 0 ? `${((stats.failed / stats.total) * 100).toFixed(1)}%` : '0%'}
                    </div>
                </div>

                <div style={{
                    background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(230, 126, 34, 0.3)'
                }}>
                    <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '5px' }}>⚠️ No Entregados</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700' }}>{stats.undelivered}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                        {stats.total > 0 ? `${((stats.undelivered / stats.total) * 100).toFixed(1)}%` : '0%'}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div style={{ 
                marginBottom: '25px',
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                alignItems: 'center'
            }}>
                <span style={{ fontWeight: '600', color: '#2c3e50', marginRight: '10px' }}>Filtrar por estado:</span>
                <button
                    onClick={() => handleFilterChange('')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: filterStatus === '' ? '#3498db' : '#ecf0f1',
                        color: filterStatus === '' ? 'white' : '#2c3e50',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s'
                    }}
                >
                    Todos
                </button>
                <button
                    onClick={() => handleFilterChange('delivered')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: filterStatus === 'delivered' ? '#27ae60' : '#ecf0f1',
                        color: filterStatus === 'delivered' ? 'white' : '#2c3e50',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    Entregados
                </button>
                <button
                    onClick={() => handleFilterChange('read')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: filterStatus === 'read' ? '#3498db' : '#ecf0f1',
                        color: filterStatus === 'read' ? 'white' : '#2c3e50',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    Leídos
                </button>
                <button
                    onClick={() => handleFilterChange('failed')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: filterStatus === 'failed' ? '#e74c3c' : '#ecf0f1',
                        color: filterStatus === 'failed' ? 'white' : '#2c3e50',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    Fallidos
                </button>
                <button
                    onClick={() => handleFilterChange('undelivered')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: filterStatus === 'undelivered' ? '#e67e22' : '#ecf0f1',
                        color: filterStatus === 'undelivered' ? 'white' : '#2c3e50',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    No Entregados
                </button>
            </div>

            {error && (
                <div style={{
                    padding: '15px',
                    background: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '8px',
                    color: '#c33',
                    marginBottom: '20px'
                }}>
                    {error}
                </div>
            )}

            {/* Messages Table */}
            <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
                overflow: 'hidden'
            }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>Teléfono</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>Dirección</th>
                                <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#2c3e50' }}>Estado Envío</th>
                                <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#2c3e50' }}>Estado Entrega</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>Fecha Envío</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>Última Act.</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {messages.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
                                        No hay mensajes para mostrar
                                    </td>
                                </tr>
                            ) : (
                                messages.map((msg) => (
                                    <tr key={msg._id} style={{ 
                                        borderBottom: '1px solid #ecf0f1',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                    >
                                        <td style={{ padding: '14px', color: '#2c3e50', fontWeight: '500' }}>
                                            {msg.phoneNumber || 'N/A'}
                                        </td>
                                        <td style={{ padding: '14px', color: '#7f8c8d', fontSize: '0.9rem' }}>
                                            {msg.sentAddress || msg.valuacionData?.address || 'N/A'}
                                        </td>
                                        <td style={{ padding: '14px', textAlign: 'center' }}>
                                            {getStatusBadge(msg.status)}
                                        </td>
                                        <td style={{ padding: '14px', textAlign: 'center' }}>
                                            {msg.deliveryStatus ? getStatusBadge(msg.deliveryStatus) : (
                                                <span style={{ color: '#95a5a6', fontSize: '0.9rem' }}>Pendiente</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '14px', color: '#7f8c8d', fontSize: '0.9rem' }}>
                                            {formatDate(msg.sentAt)}
                                        </td>
                                        <td style={{ padding: '14px', color: '#7f8c8d', fontSize: '0.9rem' }}>
                                            {formatDate(msg.lastStatusUpdate)}
                                        </td>
                                        <td style={{ padding: '14px', color: '#e74c3c', fontSize: '0.85rem' }}>
                                            {msg.deliveryError ? (
                                                <div>
                                                    <div style={{ fontWeight: '600' }}>
                                                        {msg.deliveryErrorCode || 'Error'}
                                                    </div>
                                                    <div style={{ fontSize: '0.8rem', marginTop: '2px' }}>
                                                        {msg.deliveryError}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span style={{ color: '#95a5a6' }}>-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div style={{
                        padding: '20px',
                        borderTop: '1px solid #ecf0f1',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '15px'
                    }}>
                        <div style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                            Página {pagination.currentPage} de {pagination.totalPages} 
                            ({pagination.totalMessages} mensajes totales)
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: pagination.currentPage === 1 ? '#ecf0f1' : '#3498db',
                                    color: pagination.currentPage === 1 ? '#95a5a6' : 'white',
                                    fontWeight: '600',
                                    cursor: pagination.currentPage === 1 ? 'not-allowed' : 'pointer'
                                }}
                            >
                                ← Anterior
                            </button>
                            <button
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === pagination.totalPages}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: pagination.currentPage === pagination.totalPages ? '#ecf0f1' : '#3498db',
                                    color: pagination.currentPage === pagination.totalPages ? '#95a5a6' : 'white',
                                    fontWeight: '600',
                                    cursor: pagination.currentPage === pagination.totalPages ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Siguiente →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WhatsAppMessages;
